'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Check, Zap, Layers, Crown } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    nameRu: 'Базовый',
    price: 5,
    icon: Layers,
    description: 'Вход в пространство. Основной контент и сообщество.',
    color: '#7A7470',
    borderColor: '#2A2520',
    features: [
      'Доступ к библиотеке видео',
      'Участие в сообществе',
      'Ежемесячный дайджест',
      'Базовые практики',
      'Записи прошлых сессий (частично)',
    ],
    notIncluded: [
      'Живые сессии',
      'Прямой Q&A',
      'Inner Circle контент',
    ],
  },
  {
    id: 'deep',
    name: 'Deep',
    nameRu: 'Глубокий',
    price: 15,
    icon: Zap,
    description: 'Полное погружение. Живые встречи и Q&A каждую неделю.',
    color: '#C9A96E',
    borderColor: '#C9A96E',
    featured: true,
    features: [
      'Всё из Basic',
      'Еженедельные живые сессии',
      'Q&A в прямом эфире',
      'Расширенная библиотека практик',
      'Записи всех сессий',
      'Приоритетный ответ на вопросы',
    ],
    notIncluded: [
      'Inner Circle контент',
    ],
  },
  {
    id: 'inner',
    name: 'Inner Circle',
    nameRu: 'Внутренний круг',
    price: 30,
    icon: Crown,
    description: 'Максимальная близость. Персональный доступ и эксклюзив.',
    color: '#F0EDE8',
    borderColor: '#2A2520',
    features: [
      'Всё из Deep',
      'Ежемесячные группы по 5–10 человек',
      'Эксклюзивные видео и материалы',
      'Личные сообщения (ограниченно)',
      'Ранний доступ к новым форматам',
      'Влияние на темы контента',
      'Бессрочный архив',
    ],
    notIncluded: [],
  },
];

export default function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <section id="plans" className="py-28 px-6 lg:px-10 2xl:px-16" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="max-w-screen-2xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs tracking-widest uppercase mb-3" style={{ color: '#7A7470' }}>Тарифы</p>
          <h2
            className="text-3xl md:text-4xl font-300 text-white mb-6"
            style={{ fontWeight: 300, letterSpacing: '-0.02em' }}
          >
            Выбери своё пространство
          </h2>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full" style={{ background: '#141414', border: '1px solid #2A2520' }}>
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-500 transition-all duration-200 ${billingCycle === 'monthly' ? 'bg-white text-[#0A0A0A]' : 'text-[#7A7470]'}`}
            >
              Ежемесячно
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-500 transition-all duration-200 flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-white text-[#0A0A0A]' : 'text-[#7A7470]'}`}
            >
              Ежегодно
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: billingCycle === 'yearly' ? 'rgba(201,169,110,0.15)' : 'rgba(201,169,110,0.1)', color: '#C9A96E' }}>
                −20%
              </span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 xl:gap-8">
          {TIERS?.map(tier => {
            const Icon = tier?.icon;
            const displayPrice = billingCycle === 'yearly'
              ? Math.round(tier?.price * 0.8)
              : tier?.price;

            return (
              <div
                key={tier?.id}
                className={`relative rounded-2xl p-8 transition-all duration-300 ${tier?.featured ? 'scale-[1.02]' : ''}`}
                style={{
                  background: tier?.featured ? 'rgba(201, 169, 110, 0.04)' : '#141414',
                  border: `1px solid ${tier?.borderColor}`,
                }}
              >
                {tier?.featured && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-500"
                    style={{ background: '#C9A96E', color: '#0A0A0A' }}
                  >
                    Популярный выбор
                  </div>
                )}
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <Icon size={18} style={{ color: tier?.color }} />
                  </div>
                  <div>
                    <p className="text-xs tracking-widest uppercase" style={{ color: '#7A7470' }}>
                      {tier?.nameRu}
                    </p>
                    <h3 className="text-base font-600 text-white" style={{ fontWeight: 600 }}>{tier?.name}</h3>
                  </div>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-700 tabular-nums text-white" style={{ fontWeight: 700 }}>
                      ${displayPrice}
                    </span>
                    <span className="text-sm" style={{ color: '#7A7470' }}>/ мес</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-xs mt-1" style={{ color: '#C9A96E' }}>
                      ${displayPrice * 12} в год (экономия ${(tier?.price - displayPrice) * 12})
                    </p>
                  )}
                </div>
                <p className="text-sm mb-8 leading-relaxed" style={{ color: '#7A7470' }}>{tier?.description}</p>
                <Link
                  href="/sign-up-login-screen"
                  className={`block text-center py-3.5 rounded-full text-sm font-500 transition-all duration-200 active:scale-95 mb-8 ${
                    tier?.featured
                      ? 'hover:opacity-85' :'border hover:border-opacity-60'
                  }`}
                  style={
                    tier?.featured
                      ? { background: '#C9A96E', color: '#0A0A0A' }
                      : { borderColor: '#2A2520', color: '#F0EDE8' }
                  }
                >
                  Начать
                </Link>
                <div className="space-y-3">
                  {tier?.features?.map(f => (
                    <div key={f} className="flex items-start gap-3">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(201,169,110,0.1)' }}>
                        <Check size={10} style={{ color: '#C9A96E' }} />
                      </div>
                      <span className="text-sm leading-snug" style={{ color: '#F0EDE8', opacity: 0.8 }}>{f}</span>
                    </div>
                  ))}
                  {tier?.notIncluded?.map(f => (
                    <div key={f} className="flex items-start gap-3 opacity-30">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div className="w-1.5 h-px bg-current" />
                      </div>
                      <span className="text-sm leading-snug" style={{ color: '#7A7470' }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs mt-8" style={{ color: '#7A7470' }}>
          Оплата через Stripe. Отмена в любой момент. Никаких скрытых платежей.
        </p>
      </div>
    </section>
  );
}